"use client";

import * as React from "react";
import { ArrowRight, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { AddToCartButton, EcommerceProductsProvider, ProductSortDropdown, ProductsFilter, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
declare const wvcClient: any;
function ProductsGridConsumer() {
  const {
    products,
    total,
    loading,
    error,
    refetch,
    hasNext,
    hasPrev,
    currentPage,
    totalPages,
    nextPage,
    prevPage,
    setPage
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="aspect-square bg-muted/40 animate-pulse" />)}
      </div>;
  }
  if (error) {
    return <div className="font-mono text-sm uppercase tracking-wider text-secondary">
        Failed to load transfers.{" "}
        <Button variant="link" onClick={refetch} className="px-1 font-mono text-foreground">
          Retry
        </Button>
      </div>;
  }
  if (products.length === 0) {
    return <p className="font-mono text-sm uppercase tracking-wider text-secondary">
        No transfers match your selection.
      </p>;
  }
  const pageNumbers: number[] = [];
  for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
  return <>
      <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
        {products.map((product, i) => <article key={product.id} data-index={i} className="group relative flex flex-col">
            <Link to={product.link} className="block">
              <div className="relative aspect-square overflow-hidden bg-card border border-border">
                {product.featured_image_url && <img src={product.featured_image_url} alt={product.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]" />}
                {i % 5 === 0 && <span className="absolute left-4 top-4 border border-primary bg-background/90 px-2 py-1 font-mono text-[10px] font-semibold uppercase tracking-widest text-primary">
                    Premium
                  </span>}
                {i % 7 === 0 && <span className="absolute right-4 top-4 border border-foreground bg-background/90 px-2 py-1 font-mono text-[10px] font-semibold uppercase tracking-widest text-foreground">
                    New
                  </span>}
              </div>
            </Link>

            <div className="mt-5 flex flex-col gap-2">
              {product.categories && product.categories.length > 0 && <div className="font-mono text-[11px] font-medium uppercase tracking-[0.18em] text-primary">
                  — {product.categories[0].name}
                </div>}
              <Link to={product.link}>
                <h3 className="font-default text-xl font-medium leading-tight text-foreground transition-colors hover:text-primary">
                  {product.title}
                </h3>
              </Link>
              {product.short_description && <div className="font-default text-sm text-secondary [&_p]:mb-0 [&_p:last-child]:mb-0 line-clamp-2" dangerouslySetInnerHTML={{
            __html: product.short_description
          }} />}

              <div className="mt-3 flex items-end justify-between gap-3">
                <div className="flex items-baseline gap-2">
                  {product.discounted_price !== undefined && product.discounted_price !== null ? <>
                      <span className="font-default text-lg font-semibold text-foreground">
                        ${product.discounted_price}
                      </span>
                      <span className="font-default text-sm text-secondary line-through">
                        ${product.price}
                      </span>
                    </> : <span className="font-default text-lg font-semibold text-foreground">
                      ${product.price}
                    </span>}
                </div>

                <AddToCartButton productId={product.id} variant="default" size="icon" className="w-auto" buttonClassName="bg-primary text-primary-foreground hover:bg-primary/90 h-10 w-10" text="" showQuantity={false} />
              </div>
            </div>
          </article>)}
      </div>

      {totalPages > 1 && <nav aria-label="Pagination" className="mt-16 flex items-center justify-center gap-1 border-t border-border pt-8">
          <Button variant="ghost" onClick={prevPage} disabled={!hasPrev} className="font-mono text-xs uppercase tracking-widest text-foreground disabled:opacity-30">
            Prev
          </Button>
          <div className="flex items-center">
            {pageNumbers.map((p, idx) => <div key={p} data-index={idx} className="flex items-center">
                <Button variant="ghost" size="sm" onClick={() => setPage(p)} className={p === currentPage ? "font-mono text-sm text-primary underline underline-offset-4 decoration-2" : "font-mono text-sm text-foreground hover:text-primary"}>
                  {String(p).padStart(2, "0")}
                </Button>
                {idx < pageNumbers.length - 1 && <span aria-hidden="true" className="mx-1 h-px w-3 bg-border" />}
              </div>)}
          </div>
          <Button variant="ghost" onClick={nextPage} disabled={!hasNext} className="font-mono text-xs uppercase tracking-widest text-foreground disabled:opacity-30">
            Next
            <ArrowRight className="ml-1 h-3 w-3" />
          </Button>
        </nav>}

      <div className="mt-6 text-center font-mono text-[11px] uppercase tracking-widest text-secondary">
        Showing page {String(currentPage).padStart(2, "0")} of{" "}
        {String(totalPages).padStart(2, "0")} — {total} transfers
      </div>
    </>;
}
function ProductsCountLabel() {
  const {
    total,
    loading
  } = useEcommerceProducts();
  return <div className="font-mono text-xs font-medium uppercase tracking-[0.2em] text-foreground">
      {loading ? "— Loading" : `${String(total).padStart(2, "0")} Transfers`}
    </div>;
}
export default function ShopContent() {
  const currentWPQueryParams = typeof window !== "undefined" && (window as any).wvcClient ? (window as any).wvcClient.getCurrentWPQueryParams() : {};
  const wp_query = new WP_Query(currentWPQueryParams);
  return <section data-section-id="3434" id="shop-content" className="bg-background text-foreground py-20 px-4 sm:px-6 lg:px-10">
      <div className="mx-auto max-w-7xl">
        <EcommerceProductsProvider wp_query={wp_query}>
          <div className="flex flex-col gap-10 lg:flex-row lg:gap-12">
            <aside className="w-full lg:sticky lg:top-24 lg:h-fit lg:w-1/4 lg:shrink-0">
              <div className="border border-border bg-card p-6">
                <div className="mb-6 flex items-center justify-between border-b border-border pb-4">
                  <h2 className="font-default text-xs font-semibold uppercase tracking-[0.2em] text-foreground">
                    — Filters
                  </h2>
                </div>
                <ProductsFilter />
              </div>
            </aside>

            <div className="w-full lg:w-3/4">
              <div className="mb-8 flex flex-col items-start justify-between gap-4 border-b border-border pb-6 sm:flex-row sm:items-center">
                <ProductsCountLabel />
                <div className="flex items-center gap-3">
                  <span className="hidden font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-secondary sm:inline">
                    Sort by
                  </span>
                  <ProductSortDropdown label="" />
                </div>
              </div>

              <ProductsGridConsumer />
            </div>
          </div>
        </EcommerceProductsProvider>
      </div>
    </section>;
}